/*
   Data shape for the review queue.

   1. UI rows (`QueueRow`) are what the table wants to render.
   2. Wire types (`ThreadOutWire`) mirror the backend response of
      `GET /api/v1/threads` (routes_workflow.py:42–61). `toQueueRow`
      is the adapter — the one boundary where numeric backend IDs become
      branded strings and where thread → per-current-version projection
      happens.

   Filtering, sorting and paging are the server's: the request params are
   built in `queue-query-params.ts`, and nothing here narrows a page of rows.
*/

import type { WireReviewStatus } from "./queue-review-status"

/**
 * Branded id so a raw string can't be mistaken for a ProductId at a boundary.
 * It is the product's `thread_id`, which every version shares, so a row keeps
 * its identity (and stays expanded) when a new version becomes the current one.
 */
export type ProductId = string & { readonly __brand: "ProductId" }

/**
 * The four triage tiles. Counted by the backend (`/threads/summary`), never
 * derived here: a tile groups several review statuses, and a row shows its own.
 */
export type QueueStatus = "queued" | "in_review" | "needs_sme" | "cleared"

export interface QueueRow {
  id: ProductId
  /** What to call the product in text: its name, or its reg. no. when none was found. */
  product: string
  /** The extracted name, or null when the extractor has not identified the product yet. */
  productName: string | null
  epaRegNo: string
  /** The current version's review status, shown with legacy's eight labels (or as itself). */
  status: WireReviewStatus
  /** The current version's pipeline stage; `batch_wait` is the overnight lane. */
  stage: string | null
  /** Whether the current version's findings are released to this reader (false for a client early). */
  findingsReleased: boolean
  versions: number
  /** The current version's findings — the count the Findings filter matches on. */
  findingsOutstanding: number
  findingsCorrected: number
  /** Unread messages across every version. */
  commsUnread: number
  /** Who holds the current version, from `/threads`; null when unassigned. */
  assigneeName: string | null
  /** The engagement's name, from `/threads`; null for an internal review. */
  clientName: string | null
  /** The current version's completed_at, else created_at (ISO); what Updated shows and sorts by. */
  updatedAt: string | null
  /** Every version as the backend sent it, newest first — what the expanded row lists. */
  versionList: QueueVersion[]
}

/** One version of a product, read straight off `ThreadOut.versions`. */
export interface QueueVersion {
  reviewId: number
  versionNo: number
  isCurrent: boolean
  status: WireReviewStatus
  stage: string | null
  outstanding: number
  corrected: number
  unread: number
  /** False while a client's view is held back; the counts are then not meaningful. */
  findingsReleased: boolean
}

/* ------- presentation config (data-adjacent, view-agnostic) ------- */

/** Tile captions, in the strip's order. */
export const STATUS_LABEL: Record<QueueStatus, string> = {
  queued: "Queued",
  in_review: "In review",
  needs_sme: "Needs SME",
  cleared: "Cleared",
}

export const STATUS_TONE: Record<QueueStatus, "info" | "print" | "people" | "master"> = {
  queued: "info",
  in_review: "print",
  needs_sme: "people",
  cleared: "master",
}

/* ---------------------------- wire types --------------------------- */

/** Per-version entry inside a ThreadOut. Matches routes_workflow.py:42. */
interface VersionEntryWire {
  review_id: number
  version_no: number
  status: WireReviewStatus
  stage: string | null
  outstanding: number
  corrected: number
  unread: number
  archived: boolean
  /** Absent from older backends; treated as released, as the backend defaults it. */
  findings_released?: boolean
}

/** GET /api/v1/threads returns list<ThreadOut>. Exported so `api/threads.ts`
 * can type the request result without re-declaring the wire. */
export interface ThreadOutWire {
  thread_id: string
  reg_nr: string
  product_name: string | null
  client_id: number | null
  current_review_id: number
  // The current version's, filled by the backend (absent from older backends).
  client_name?: string | null
  assignee_id?: number | null
  assignee_name?: string | null
  updated_at?: string | null
  version_count: number
  versions: [VersionEntryWire, ...VersionEntryWire[]]
}

/**
 * ThreadOut → QueueRow. The current version speaks for the row: its status,
 * stage, findings, and — from the backend — its assignee, engagement and
 * updated time. Unread is summed over every version, as legacy shows it, since
 * a message on an older version is still a message nobody has read.
 */
export function toQueueRow(t: ThreadOutWire): QueueRow {
  const current = t.versions.find((v) => v.review_id === t.current_review_id) ?? t.versions.at(-1)
  return {
    // The thread, not its current review: that changes with every new version.
    id: t.thread_id as ProductId,
    product: t.product_name ?? t.reg_nr,
    productName: t.product_name,
    epaRegNo: t.reg_nr,
    // A thread always has a version; the fallbacks only keep the types honest.
    status: current?.status ?? "pending",
    stage: current?.stage ?? null,
    findingsReleased: current?.findings_released ?? true,
    versions: t.version_count,
    findingsOutstanding: current?.outstanding ?? 0,
    findingsCorrected: current?.corrected ?? 0,
    commsUnread: t.versions.reduce((a, v) => a + v.unread, 0),
    assigneeName: t.assignee_name ?? null,
    clientName: t.client_name ?? null,
    updatedAt: t.updated_at ?? null,
    versionList: toQueueVersions(t, current?.review_id),
  }
}

/** The thread's real versions, newest first (legacy's expanded order). */
function toQueueVersions(t: ThreadOutWire, currentId: number | undefined): QueueVersion[] {
  return [...t.versions]
    .sort((a, b) => b.version_no - a.version_no)
    .map((v) => ({
      reviewId: v.review_id,
      versionNo: v.version_no,
      isCurrent: v.review_id === currentId,
      status: v.status,
      stage: v.stage,
      outstanding: v.outstanding,
      corrected: v.corrected,
      unread: v.unread,
      findingsReleased: v.findings_released ?? true,
    }))
}
